import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import {
  Bell, Users, TrendingUp, Phone, Star, Crown, Gift, Target,
  AlertTriangle, Calendar, DollarSign, Award, ChevronRight,
  MessageCircle, Clock, Zap, Heart, RefreshCw, ArrowUp, ArrowDown,
  Cake, ShieldCheck, Trophy, Sparkles, Store, Truck, UtensilsCrossed,
  FileText, Download
} from 'lucide-react';

// Tier colors and icons
const TIER_CONFIG = {
  diamante: { color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50', icon: '💎', border: 'border-purple-300' },
  oro: { color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', icon: '🥇', border: 'border-amber-300' },
  plata: { color: 'bg-gray-400', textColor: 'text-gray-700', bgLight: 'bg-gray-50', icon: '🥈', border: 'border-gray-300' },
  bronce: { color: 'bg-orange-700', textColor: 'text-orange-800', bgLight: 'bg-orange-50', icon: '🥉', border: 'border-orange-300' }
};

const URGENCY_CONFIG = {
  overdue: { color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', label: 'Atrasado' },
  today: { color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', label: 'Hoy' },
  soon: { color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', label: 'Pronto' },
  this_week: { color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-50', label: 'Esta semana' },
  later: { color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', label: 'Después' }
};

// Origin configuration
const ORIGIN_CONFIG = {
  comedor: { icon: '🍽️', label: 'Comedor', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', border: 'border-blue-300', IconComponent: UtensilsCrossed },
  mostrador: { icon: '🛍️', label: 'Mostrador', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', border: 'border-green-300', IconComponent: Store },
  domicilio: { icon: '🏍️', label: 'Domicilio', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', border: 'border-orange-300', IconComponent: Truck },
  mixto: { icon: '🔄', label: 'Mixto', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50', border: 'border-purple-300', IconComponent: RefreshCw }
};

export default function CRMIntelligence() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [tierDistribution, setTierDistribution] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [clvRanking, setClvRanking] = useState(null);
  const [customersByOrigin, setCustomersByOrigin] = useState(null);
  const [originStats, setOriginStats] = useState(null);
  const [selectedOrigin, setSelectedOrigin] = useState('all');
  
  // Date filters - default to 7 days
  const [dateFilter, setDateFilter] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  
  // Calculate days based on dateFilter - properly memoized
  const days = useMemo(() => {
    switch(dateFilter) {
      case 'today': return 1;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      case 'year': return 365;
      case 'custom': return 365;
      default: return 7;
    }
  }, [dateFilter]);
  
  // Build date params for API calls - properly memoized
  const dateParams = useMemo(() => {
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      return `start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    return `days=${days}`;
  }, [dateFilter, customStartDate, customEndDate, days]);

  const fetchDashboard = useCallback(async () => {
    try {
      const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
      const res = await api.get(`/crm-intelligence/dashboard?${dateParams}${originParam}`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [api, dateParams, selectedOrigin]);

  const fetchAlerts = useCallback(async () => {
    try {
      const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
      const res = await api.get(`/crm-intelligence/alerts?${dateParams}${originParam}`);
      setAlerts(res.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [api, dateParams, selectedOrigin]);

  const fetchTierDistribution = useCallback(async () => {
    try {
      const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
      const res = await api.get(`/crm-intelligence/tiers/distribution?${dateParams}${originParam}`);
      setTierDistribution(res.data);
    } catch (error) {
      console.error('Error fetching tier distribution:', error);
    }
  }, [api, dateParams, selectedOrigin]);

  const fetchPredictions = useCallback(async () => {
    try {
      const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
      const res = await api.get(`/crm-intelligence/predictions?limit=50&${dateParams}${originParam}`);
      setPredictions(res.data);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  }, [api, dateParams, selectedOrigin]);

  const fetchClvRanking = useCallback(async () => {
    try {
      const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
      const res = await api.get(`/crm-intelligence/clv/ranking?limit=30&${dateParams}${originParam}`);
      setClvRanking(res.data);
    } catch (error) {
      console.error('Error fetching CLV ranking:', error);
    }
  }, [api, dateParams, selectedOrigin]);

  const fetchCustomersByOrigin = useCallback(async () => {
    try {
      const res = await api.get(`/crm-intelligence/customers-by-origin?origin_filter=${selectedOrigin}&${dateParams}`);
      setCustomersByOrigin(res.data);
    } catch (error) {
      console.error('Error fetching customers by origin:', error);
    }
  }, [api, selectedOrigin, dateParams]);

  const fetchOriginStats = useCallback(async () => {
    try {
      const res = await api.get(`/crm-intelligence/origin-stats?${dateParams}`);
      setOriginStats(res.data);
    } catch (error) {
      console.error('Error fetching origin stats:', error);
    }
  }, [api, dateParams]);

  // Main data loading effect - triggers on filter changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const originParam = selectedOrigin !== 'all' ? `&origin_filter=${selectedOrigin}` : '';
        
        // Build date params
        let params = dateFilter === 'custom' && customStartDate && customEndDate
          ? `start_date=${customStartDate}&end_date=${customEndDate}`
          : `days=${days}`;
        
        // Fetch all data in parallel with current params
        const [dashboardRes, alertsRes, tiersRes, predictionsRes, clvRes, originsRes, statsRes] = await Promise.all([
          api.get(`/crm-intelligence/dashboard?${params}${originParam}`),
          api.get(`/crm-intelligence/alerts?${params}${originParam}`),
          api.get(`/crm-intelligence/tiers/distribution?${params}${originParam}`),
          api.get(`/crm-intelligence/predictions?limit=50&${params}${originParam}`),
          api.get(`/crm-intelligence/clv/ranking?limit=30&${params}${originParam}`),
          api.get(`/crm-intelligence/customers-by-origin?origin_filter=${selectedOrigin}&${params}`),
          api.get(`/crm-intelligence/origin-stats?${params}`)
        ]);
        
        setDashboard(dashboardRes.data);
        setAlerts(alertsRes.data);
        setTierDistribution(tiersRes.data);
        setPredictions(predictionsRes.data);
        setClvRanking(clvRes.data);
        setCustomersByOrigin(originsRes.data);
        setOriginStats(statsRes.data);
      } catch (error) {
        console.error('Error loading CRM data:', error);
      }
      setLoading(false);
    };
    loadData();
  }, [api, dateFilter, days, customStartDate, customEndDate, selectedOrigin]);

  const openWhatsApp = (phone, customerName, message) => {
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const defaultMsg = message || `¡Hola ${customerName}! 👋 Te contactamos de Mr. Frogs. Tenemos una oferta especial para ti.`;
    window.open(`https://wa.me/52${cleanPhone}?text=${encodeURIComponent(defaultMsg)}`, '_blank');
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchAlerts(),
      fetchTierDistribution(),
      fetchPredictions(),
      fetchClvRanking(),
      fetchCustomersByOrigin(),
      fetchOriginStats()
    ]);
    setLoading(false);
    toast.success('Datos actualizados');
  };

  const exportOriginPDF = async (origin) => {
    try {
      toast.info('Generando PDF...');
      const url = `/crm-reports/customers-by-origin-pdf?origin=${origin}&${dateParams}`;
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `clientes_por_origen_${origin}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  // Alert Card Component
  const AlertCard = ({ alert, type }) => {
    const tierConfig = TIER_CONFIG[alert.tier?.tier] || TIER_CONFIG.bronce;
    
    return (
      <div className={`p-4 rounded-lg border-l-4 ${tierConfig.border} ${tierConfig.bgLight} mb-3`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg">{tierConfig.icon}</span>
              <span className="font-bold">{alert.name}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierConfig.color} text-white`}>
                {alert.tier?.label}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
              <Phone className="w-3 h-3" /> {alert.phone}
              <span className="text-green-600 font-medium">${alert.total_spent?.toLocaleString()}</span>
              <span className="text-gray-400">• {alert.visit_count} visitas</span>
            </p>
            <p className="text-sm mt-2 font-medium text-gray-800">
              {alert.alert_reason}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              💡 {alert.suggested_action}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => openWhatsApp(alert.phone, alert.name)}
            className="bg-green-500 hover:bg-green-600 text-white shrink-0"
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            WhatsApp
          </Button>
        </div>
      </div>
    );
  };

  // Prediction Row Component
  const PredictionRow = ({ customer }) => {
    const tierConfig = TIER_CONFIG[customer.tier?.tier] || TIER_CONFIG.bronce;
    const urgencyConfig = URGENCY_CONFIG[customer.prediction?.urgency] || URGENCY_CONFIG.later;
    
    return (
      <div className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg border-b">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${tierConfig.color}`}>
          {customer.name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{customer.name}</span>
            <span className="text-sm">{tierConfig.icon}</span>
          </div>
          <p className="text-sm text-gray-500">
            Cada {customer.avg_frequency_days} días • ${customer.total_spent?.toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${urgencyConfig.bgLight} ${urgencyConfig.textColor}`}>
            {customer.prediction?.urgency_label}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => openWhatsApp(customer.phone, customer.name)}
          className="text-green-600"
        >
          <MessageCircle className="w-5 h-5" />
        </Button>
      </div>
    );
  };

  if (loading && !dashboard) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <Sparkles className="w-8 h-8" />
                  CRM Inteligente
                </h1>
                <p className="text-white/80 mt-1">Sistema de crecimiento de ventas y fidelización</p>
              </div>
              <Button onClick={refreshData} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Quick Stats */}
            {dashboard && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-white/70 text-sm">Acciones Hoy</p>
                  <p className="text-3xl font-bold">{dashboard.alert_summary?.total_actions || 0}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-white/70 text-sm">Clientes Totales</p>
                  <p className="text-3xl font-bold">{dashboard.total_customers || 0}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-white/70 text-sm">Valor Anual (CLV)</p>
                  <p className="text-3xl font-bold">${(dashboard.total_annual_clv || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-white/70 text-sm">CLV Promedio</p>
                  <p className="text-3xl font-bold">${(dashboard.avg_annual_clv || 0).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {[
              { id: 'alerts', label: 'Alertas', icon: Bell, count: dashboard?.alert_summary?.total_actions },
              { id: 'origin', label: 'Por Origen', icon: UtensilsCrossed, count: customersByOrigin?.total_customers },
              { id: 'predictions', label: 'Predicciones', icon: Target, count: predictions?.urgency_counts?.overdue + predictions?.urgency_counts?.today },
              { id: 'tiers', label: 'Tiers VIP', icon: Crown },
              { id: 'clv', label: 'Valor Cliente', icon: DollarSign },
              { id: 'points', label: 'Puntos', icon: Gift }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {tab.count}
                  </span>
                )}
              </Button>
            ))}
          </div>
          
          {/* Filters Bar */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Date Period Buttons */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { value: 'today', label: 'Hoy', highlight: true },
                    { value: '7d', label: '7 días' },
                    { value: '30d', label: '30 días' },
                    { value: '90d', label: '3 meses' },
                    { value: 'year', label: 'Año' },
                    { value: 'custom', label: 'Personalizado', icon: Calendar }
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      variant={dateFilter === opt.value ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => { 
                        setDateFilter(opt.value); 
                        setShowCustomDates(opt.value === 'custom'); 
                      }}
                      className={`text-xs px-3 ${opt.highlight && dateFilter === opt.value ? 'bg-green-600 hover:bg-green-700' : ''} ${opt.highlight && dateFilter !== opt.value ? 'text-green-600 font-semibold' : ''}`}
                    >
                      {opt.icon && <opt.icon className="w-3 h-3 mr-1" />}
                      {opt.label}
                    </Button>
                  ))}
                </div>
                
                {/* Origin Filter */}
                <Select value={selectedOrigin} onValueChange={setSelectedOrigin}>
                  <SelectTrigger className="w-48">
                    <UtensilsCrossed className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrar por origen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los orígenes</SelectItem>
                    <SelectItem value="dine_in">🍽️ Comedor (Mesas)</SelectItem>
                    <SelectItem value="takeout">🛍️ Mostrador</SelectItem>
                    <SelectItem value="delivery">🛵 Domicilio</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Active Filter Indicators */}
                {(selectedOrigin !== 'all' || dateFilter !== '7d') && (
                  <div className="flex items-center gap-2 text-sm text-purple-600">
                    <span className="font-medium">Filtros activos:</span>
                    {dateFilter !== '7d' && (
                      <span className={`px-2 py-1 rounded ${dateFilter === 'today' ? 'bg-green-100 text-green-700' : 'bg-purple-100'}`}>
                        {dateFilter === 'custom' && customStartDate && customEndDate 
                          ? `${customStartDate} a ${customEndDate}` 
                          : dateFilter === 'today' ? '📅 Hoy' : dateFilter}
                      </span>
                    )}
                    {selectedOrigin !== 'all' && (
                      <span className="px-2 py-1 bg-purple-100 rounded">
                        {selectedOrigin === 'dine_in' ? '🍽️ Comedor' : 
                         selectedOrigin === 'takeout' ? '🛍️ Mostrador' : '🛵 Domicilio'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Custom Date Range */}
              {showCustomDates && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Desde:</span>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="px-3 py-2 border rounded-lg bg-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Hasta:</span>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="px-3 py-2 border rounded-lg bg-white"
                      />
                    </div>
                    <Button 
                      size="sm" 
                      onClick={refreshData}
                      disabled={!customStartDate || !customEndDate}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tab Content */}
          {activeTab === 'alerts' && alerts && (
            <div className="space-y-6">
              {/* Alert Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-500 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-700">{alerts.summary?.overdue_purchases || 0}</p>
                        <p className="text-xs text-red-600">Compras Atrasadas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500 rounded-lg">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-orange-700">{alerts.summary?.should_buy_today || 0}</p>
                        <p className="text-xs text-orange-600">Deberían Comprar Hoy</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-pink-200 bg-pink-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-pink-500 rounded-lg">
                        <Cake className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-pink-700">{alerts.summary?.birthdays_this_week || 0}</p>
                        <p className="text-xs text-pink-600">Cumpleaños Semana</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-700">{alerts.summary?.vip_inactive || 0}</p>
                        <p className="text-xs text-purple-600">VIP Inactivos</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Alert Lists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Should Buy Today */}
                {alerts.alerts?.should_buy_today?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-orange-700">
                        <Clock className="w-5 h-5" />
                        Deberían Comprar HOY ({alerts.alerts.should_buy_today.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.should_buy_today.map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="today" />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Overdue Purchases */}
                {alerts.alerts?.overdue_purchases?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        Compras Atrasadas ({alerts.alerts.overdue_purchases.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.overdue_purchases.slice(0, 10).map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="overdue" />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Birthdays */}
                {alerts.alerts?.birthdays_this_week?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-pink-700">
                        <Cake className="w-5 h-5" />
                        Cumpleaños Esta Semana ({alerts.alerts.birthdays_this_week.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.birthdays_this_week.map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="birthday" />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* VIP Inactive */}
                {alerts.alerts?.vip_inactive?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-purple-700">
                        <Crown className="w-5 h-5" />
                        VIP Inactivos ({alerts.alerts.vip_inactive.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.vip_inactive.map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="vip" />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* High Value at Risk */}
                {alerts.alerts?.high_value_at_risk?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-amber-700">
                        <DollarSign className="w-5 h-5" />
                        Alto Valor en Riesgo ({alerts.alerts.high_value_at_risk.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.high_value_at_risk.map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="high_value" />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* New to Nurture */}
                {alerts.alerts?.new_to_nurture?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-blue-700">
                        <Heart className="w-5 h-5" />
                        Nuevos por Fidelizar ({alerts.alerts.new_to_nurture.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-h-96 overflow-y-auto">
                      {alerts.alerts.new_to_nurture.map((alert, idx) => (
                        <AlertCard key={idx} alert={alert} type="new" />
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {activeTab === 'origin' && (
            <div className="space-y-6">
              {/* Origin Stats Summary */}
              {originStats && (
                <div className="grid grid-cols-3 gap-4">
                  {['comedor', 'mostrador', 'domicilio'].map(origin => {
                    const config = ORIGIN_CONFIG[origin];
                    const stats = originStats.stats?.[origin] || { orders: 0, revenue: 0, unique_customers: 0 };
                    return (
                      <Card key={origin} className={`${config.bgLight} ${config.border} border-2`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{config.icon}</span>
                              <span className={`font-bold ${config.textColor}`}>{config.label}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => exportOriginPDF(origin)}
                              className={`${config.textColor} border-current`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-gray-500">Clientes</p>
                              <p className="font-bold text-lg">{stats.unique_customers}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Órdenes</p>
                              <p className="font-bold text-lg">{stats.orders}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Ingresos</p>
                              <p className="font-bold text-lg text-green-600">${stats.revenue?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Ticket Prom.</p>
                              <p className="font-bold text-lg">${stats.avg_ticket?.toLocaleString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Filter and Actions */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Filtrar por origen:</span>
                      <Select value={selectedOrigin} onValueChange={setSelectedOrigin}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="comedor">🍽️ Comedor</SelectItem>
                          <SelectItem value="mostrador">🛍️ Mostrador</SelectItem>
                          <SelectItem value="domicilio">🏍️ Domicilio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <Button variant="outline" onClick={() => exportOriginPDF(selectedOrigin)}>
                        <Download className="w-4 h-4 mr-2" />
                        Descargar PDF
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Lists by Origin */}
              {customersByOrigin && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {['comedor', 'mostrador', 'domicilio'].map(origin => {
                    const config = ORIGIN_CONFIG[origin];
                    const customers = customersByOrigin.customers?.[origin] || [];
                    const summary = customersByOrigin.summary?.[origin] || { count: 0, total_value: 0 };
                    
                    if (selectedOrigin !== 'all' && selectedOrigin !== origin) return null;
                    
                    return (
                      <Card key={origin} className={`${config.border} border-2`}>
                        <CardHeader className={`${config.bgLight} pb-3`}>
                          <div className="flex items-center justify-between">
                            <CardTitle className={`flex items-center gap-2 ${config.textColor}`}>
                              <span className="text-xl">{config.icon}</span>
                              {config.label} ({summary.count})
                            </CardTitle>
                            <span className="text-sm font-bold text-green-600">
                              ${summary.total_value?.toLocaleString()}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="max-h-96 overflow-y-auto p-0">
                          {customers.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">Sin clientes</p>
                          ) : (
                            customers.slice(0, 15).map((customer, idx) => {
                              const tierConfig = TIER_CONFIG[customer.tier?.tier] || TIER_CONFIG.bronce;
                              return (
                                <div key={idx} className="flex items-center gap-3 p-3 border-b hover:bg-gray-50">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${tierConfig.color}`}>
                                    {customer.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{customer.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {customer.days_inactive > 0 ? `Inactivo ${customer.days_inactive}d` : 'Activo'} • {customer.visit_count} visitas
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-green-600 text-sm">${customer.total_spent?.toLocaleString()}</p>
                                    <p className="text-xs text-gray-400">
                                      C:{customer.origin_breakdown?.comedor} M:{customer.origin_breakdown?.mostrador} D:{customer.origin_breakdown?.domicilio}
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openWhatsApp(customer.phone, customer.name)}
                                    className="text-green-600 shrink-0"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              );
                            })
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'predictions' && predictions && (
            <div className="space-y-6">
              {/* Urgency Summary */}
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(predictions.urgency_counts || {}).map(([urgency, count]) => {
                  const config = URGENCY_CONFIG[urgency] || URGENCY_CONFIG.later;
                  return (
                    <Card key={urgency} className={config.bgLight}>
                      <CardContent className="p-4 text-center">
                        <p className={`text-3xl font-bold ${config.textColor}`}>{count}</p>
                        <p className="text-sm text-gray-600">{config.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Predictions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Predicción de Próxima Compra
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {predictions.predictions?.map((customer, idx) => (
                      <PredictionRow key={idx} customer={customer} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'tiers' && tierDistribution && (
            <div className="space-y-6">
              {/* Tier Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['diamante', 'oro', 'plata', 'bronce'].map(tier => {
                  const config = TIER_CONFIG[tier];
                  const data = tierDistribution.distribution?.[tier] || { count: 0, total_value: 0 };
                  return (
                    <Card key={tier} className={`${config.bgLight} ${config.border} border-2`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-3xl">{config.icon}</span>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${config.textColor}`}>{data.count}</p>
                            <p className="text-xs text-gray-600">clientes</p>
                          </div>
                        </div>
                        <p className={`text-lg font-semibold mt-2 ${config.textColor}`}>
                          ${data.total_value?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">valor total</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Tier Customer Lists */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {['diamante', 'oro', 'plata', 'bronce'].map(tier => {
                  const config = TIER_CONFIG[tier];
                  const data = tierDistribution.distribution?.[tier] || { customers: [] };
                  if (data.customers?.length === 0) return null;
                  
                  return (
                    <Card key={tier} className={`${config.border} border-2`}>
                      <CardHeader className={config.bgLight}>
                        <CardTitle className={`flex items-center gap-2 ${config.textColor}`}>
                          <span className="text-2xl">{config.icon}</span>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)} ({data.customers?.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="max-h-64 overflow-y-auto">
                        {data.customers?.map((customer, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                            <div>
                              <p className="font-medium">{customer.name}</p>
                              <p className="text-sm text-gray-500">{customer.visit_count} visitas</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">${customer.total_spent?.toLocaleString()}</p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openWhatsApp(customer.phone, customer.name)}
                                className="text-green-600"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'clv' && clvRanking && (
            <div className="space-y-6">
              {/* CLV Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                  <CardContent className="p-6">
                    <DollarSign className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-3xl font-bold">${clvRanking.total_annual_clv?.toLocaleString()}</p>
                    <p className="text-white/80">Valor Total Anual</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                  <CardContent className="p-6">
                    <Users className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-3xl font-bold">{clvRanking.total_customers}</p>
                    <p className="text-white/80">Clientes con Historial</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                  <CardContent className="p-6">
                    <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-3xl font-bold">${clvRanking.avg_annual_clv?.toLocaleString()}</p>
                    <p className="text-white/80">CLV Promedio</p>
                  </CardContent>
                </Card>
              </div>

              {/* CLV Ranking Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Ranking por Valor del Cliente (CLV)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">#</th>
                          <th className="text-left p-3">Cliente</th>
                          <th className="text-left p-3">Tier</th>
                          <th className="text-right p-3">Total Gastado</th>
                          <th className="text-right p-3">Visitas</th>
                          <th className="text-right p-3">Ticket Prom.</th>
                          <th className="text-right p-3">CLV Mensual</th>
                          <th className="text-right p-3">CLV Anual</th>
                          <th className="text-center p-3">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clvRanking.rankings?.map((customer, idx) => {
                          const tierConfig = TIER_CONFIG[customer.tier?.tier] || TIER_CONFIG.bronce;
                          return (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                {idx < 3 ? (
                                  <span className="text-xl">{['🥇', '🥈', '🥉'][idx]}</span>
                                ) : (
                                  <span className="text-gray-400">{idx + 1}</span>
                                )}
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{customer.name}</p>
                                <p className="text-sm text-gray-500">{customer.phone}</p>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${tierConfig.color} text-white`}>
                                  {tierConfig.icon} {customer.tier?.label}
                                </span>
                              </td>
                              <td className="p-3 text-right font-medium">${customer.total_spent?.toLocaleString()}</td>
                              <td className="p-3 text-right">{customer.visit_count}</td>
                              <td className="p-3 text-right">${customer.clv?.avg_purchase?.toLocaleString()}</td>
                              <td className="p-3 text-right text-blue-600 font-medium">
                                ${customer.clv?.monthly_value?.toLocaleString()}
                              </td>
                              <td className="p-3 text-right text-green-600 font-bold">
                                ${customer.clv?.annual_value?.toLocaleString()}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openWhatsApp(customer.phone, customer.name)}
                                  className="text-green-600"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'points' && (
            <div className="space-y-6">
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Gift className="w-12 h-12" />
                    <div>
                      <h2 className="text-2xl font-bold">Sistema de Puntos y Recompensas</h2>
                      <p className="text-white/80">Programa de lealtad para clientes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6 text-center">
                    <Star className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                    <h3 className="text-xl font-bold">1 punto por $1</h3>
                    <p className="text-gray-600">Los clientes acumulan puntos por cada peso gastado</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Gift className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <h3 className="text-xl font-bold">500 puntos = Recompensa</h3>
                    <p className="text-gray-600">Canjear por productos gratis o descuentos</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Zap className="w-12 h-12 mx-auto text-purple-500 mb-3" />
                    <h3 className="text-xl font-bold">Multiplicadores VIP</h3>
                    <p className="text-gray-600">Diamante: 3x • Oro: 2x • Plata: 1.5x</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Configuración del Programa</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    El sistema de puntos está integrado. Los puntos se calculan automáticamente basados en el gasto total del cliente.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-medium text-amber-800">💡 Tip para maximizar ventas:</p>
                    <p className="text-amber-700 text-sm mt-1">
                      Menciona los puntos acumulados cuando contactes clientes. Ejemplo: &quot;¡Hola Juan! Tienes 450 puntos, 
                      solo te faltan 50 para tu recompensa gratis. ¿Te gustaría venir hoy?&quot;
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
